import { useMemo, useState } from 'react';
import { Modal, Form, message, Result, Spin, ModalProps } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useBoolean, useMemoizedFn } from 'ahooks';
import styled from '@emotion/styled';
import { authByDomain, TokopediaStoreType } from 'services/api/tokopedia';
import posthog from '@leyan/lytics';
import TokoRegForm2, { TokoRegForm2Props } from './TokoRegForm2';

const ModalDispute = styled(Modal)`
  .ant-modal-body {
    position: relative;
    padding-top: 60px;
  }
`;

export interface EditShopModalProps
  extends Omit<ModalProps, 'onOk' | 'cancelButtonProps' | 'okButtonProps'> {
  onSuccess?: () => void;
  onDone?: () => void;
  initialValues?: TokoRegForm2Props['initialValues'];
}

const EditShopModal = ({
  initialValues,
  onDone,
  onSuccess,
  onCancel,
  ...otherProps
}: EditShopModalProps) => {
  const { t } = useTranslate();
  const [view, setViews] = useState<'form' | 'success' | 'error'>('form');
  const [submitting, submittingHandler] = useBoolean(false);

  const [form] = Form.useForm();
  return (
    <ModalDispute
      destroyOnClose
      title={t('ViewShops.添加授权店铺')}
      centered
      onOk={useMemoizedFn(() => {
        if (view === 'form') {
          form.submit();
          return;
        }
        onDone?.();
      })}
      onCancel={onCancel}
      width={560}
      okText={t('translation.确定')}
      cancelText={t('translation.取消')}
      cancelButtonProps={{ loading: submitting, style: { display: view === 'form' ? '' : 'none' } }}
      okButtonProps={{ loading: submitting }}
      {...otherProps}
    >
      <Spin spinning={submitting}>
        {useMemo(() => {
          if (view === 'success') {
            return (
              <Result
                status="success"
                title={t('WhatsApp.已完成添加')}
                subTitle={t('ViewShops.toko.auth.success.confirm')}
              />
            );
          }
          return (
            <TokoRegForm2
              form={form}
              initialValues={{
                storeType: TokopediaStoreType.旗舰店,
                ...initialValues,
              }}
              onFinish={(values) => {
                submittingHandler.setTrue();
                authByDomain(values)
                  .then(() => {
                    setViews('success');
                    message.success(t('modals.设置成功'));
                    onSuccess?.();
                  })
                  .catch((e) => {
                    posthog.capture('$tokopedia', {
                      custRreport: {
                        opType: 'save',
                        status: 'err',
                        errTip: e.message,
                        reqData: values,
                      },
                    });

                    message.error(t('translation.操作失败，请稍后再试'));
                  })
                  .finally(() => {
                    submittingHandler.setFalse();
                  });
              }}
            />
          );
        }, [form, initialValues, onSuccess, submittingHandler, t, view])}
      </Spin>
    </ModalDispute>
  );
};

export default EditShopModal;
